import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import SEO from "@/components/site/SEO";
import PageHero from "@/components/site/PageHero";
import CTASection from "@/components/site/CTASection";
import FAQSection from "@/components/site/FAQSection";
import { PD_AREAS, FAQS_PD } from "@/data/content";

const PATHWAY_CARDS = [
  {
    title: "For individuals",
    body: "Build safety-critical behaviours, operational judgement and professional confidence for rail careers and progression.",
    to: "/for-individuals",
    cta: "Explore individual pathways",
  },
  {
    title: "For organisations",
    body: "Develop instructors, managers and operational teams through structured, evidence-led professional development.",
    to: "/for-organisations",
    cta: "Explore organisational support",
  },
  {
    title: "Communities and education",
    body: "Support colleges and education partners with rail safety and careers resources that build employability and risk awareness.",
    to: "/communities",
    cta: "Explore community resources",
  },
];

export default function ProfessionalDevelopment() {
  return (
    <>
      <SEO
        title="Professional Development | UKTDA"
        description="Railway professional development in non-technical skills, human factors, fatigue, communication, competence and incident learning for individuals and organisations."
        path="/professional-development"
      />

      <PageHero
        eyebrow="Professional development"
        title="Professional development for safer, stronger railway performance."
        intro="UKTDA professional development combines operational realism with safety-critical thinking across non-technical skills, human factors, communication, fatigue and competence management."
      />

      <section className="py-20 md:py-28 bg-white" data-testid="pd-core-areas">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Core development areas</p>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Practical capability development across people, performance and safety.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              These areas are delivered as focused sessions or integrated programmes depending
              on learner needs, role scope and operational risk context.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {PD_AREAS.map((area) => (
              <article
                key={area.title}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-7"
              >
                <h3 className="font-display font-bold text-xl text-navy">{area.title}</h3>
                <p className="mt-4 text-slate-600 leading-relaxed">{area.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-slate-50" data-testid="pd-pathways">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Where to start</p>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Choose the route that best fits your development goals.
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {PATHWAY_CARDS.map((card) => (
              <Link
                key={card.title}
                to={card.to}
                className="group rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition hover:-translate-y-0.5 hover:border-safety/60"
              >
                <h3 className="font-display font-bold text-xl text-navy">{card.title}</h3>
                <p className="mt-4 text-slate-600 leading-relaxed">{card.body}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-navy group-hover:text-safety">
                  {card.cta} <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FAQSection
        items={FAQS_PD}
        eyebrow="Professional development FAQ"
        title="Frequently asked questions about UKTDA professional development"
        intro="Answers to common questions about course focus, delivery and how professional development supports railway performance."
        schemaId="faq-professional-development"
      />

      <CTASection
        eyebrow="Plan development"
        title="Discuss a professional development pathway with UKTDA."
        intro="Tell us your role, context and capability goals and we will help you build a practical development route."
        primary={{ label: "Contact us", to: "/contact", testId: "pd-cta-contact" }}
        secondary={{ label: "View training", to: "/training", testId: "pd-cta-training" }}
      />
    </>
  );
}
